import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { saveAs } from 'file-saver';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { AdminHiddenDocumentsApiService } from 'src/app/services/admin-hidden-documents-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { DocTypes } from 'src/app/shared/doc-list-entry';
import {
  HiddenDocsLogin, HiddenDocsResponse, HiddenDocsRow, UnlistedDocsResponse, UnlistedDocsRow,
  hiddenReasonLabel, hiddenReasonTooltip
} from 'src/app/shared/admin-hidden-documents';

interface LoginOption extends HiddenDocsLogin { label: string; }

@Component({
  selector: 'app-hidden-documents',
  templateUrl: './hidden-documents.component.html',
  styleUrls: ['./hidden-documents.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Admin-only inspector: for one login (or a what-if ladder) and a date window,
 * lists the documents that login would NOT see once the `DocumentList`
 * access-group filter is switched on, with the reason codes the API returns
 * (`GET /AdminHiddenDocuments/*`, spec: docs/PROMPT_admin-hidden-documents-panel.md).
 *
 * The route is gated by `AuthGuard` with `portal-admin`, and this component
 * re-checks that role (a hidden route is not a security boundary — the API's
 * `realm:portal-admin` is). It deliberately does NOT reuse
 * `ListDocumentsForClientComponent` (tied to a client id, balances and
 * per-component localStorage keys); it reuses its column set and row classes.
 * Extracting a shared table component is a recorded follow-up (B-51).
 */
export class HiddenDocumentsComponent implements OnInit, OnDestroy {

  /** Window presets; the API's own lookback cap rejects anything older than 30 years. */
  readonly ERA_START = '1998-01-01';
  readonly PAGE_SIZES = [25, 50, 100, 200];

  logins: LoginOption[] = [];
  selectedLogin: LoginOption | null = null;
  fromDate: Date = HiddenDocumentsComponent.monthsAgo(3);
  ladderOverride: number | null = null;
  docTypes: DocTypes[] = [];
  selectedDocTypes: DocTypes[] = [];

  result: HiddenDocsResponse | null = null;
  rows: HiddenDocsRow[] = [];
  loading = false;

  /** "Unlisted documents" section: loads only when opened, for the applied window/types, never the login/ladder. */
  unlistedCollapsed = true;
  unlisted: UnlistedDocsResponse | null = null;
  unlistedRows: UnlistedDocsRow[] = [];
  unlistedLoading = false;
  unlistedFirst = 0;
  unlistedRowsPerPage = 25;
  /** Position/size of the page actually displayed; restored if a page request fails, so the paginator never reports a page whose rows are not shown. */
  private unlistedShownFirst = 0;
  private unlistedShownRows = 25;
  private shownFirst = 0;
  private shownRows = 50;
  first = 0;
  rowsPerPage = 50;

  /** Component-lifetime teardown (repo convention: `destroy$` + `takeUntil`). */
  private destroy$ = new Subject<void>();
  /** Cancel-and-replace signals: emitting cancels the in-flight request so a stale response cannot overwrite newer results. */
  private pageCancel$ = new Subject<void>();
  private unlistedCancel$ = new Subject<void>();
  /** Bumped by every explicit Load; each response is tagged with the generation it was requested under, so a
   *  listed figure from one Load is never combined with an unlisted figure from another. */
  private loadGen = 0;
  private resultGen = 0;
  private unlistedGen = 0;
  /** The query as it was when Load was pressed. Paging uses this, never the live (editable) controls. */
  private applied: { login?: string; ladder: number | null; fromDate: string; docTypes: number[] } | null = null;

  constructor(
    private api: AdminHiddenDocumentsApiService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private messageService: MessageService,
    private titleService: Title,
    private router: Router,
    private location: Location,
  ) { }

  ngOnInit(): void {
    this.globalsettingsservice.getGlobalSettings();
    // Same role the route and the API require; navigate away rather than show a table that would only 403.
    if (!this.globalsettingsservice.hasSomeRoles('portal-admin')) {
      this.router.navigate(['/']);
      return;
    }
    this.titleService.setTitle('Hidden documents');

    this.api.getHiddenDocsLogins().pipe(takeUntil(this.destroy$)).subscribe({
      next: logins => this.logins = logins.map(l => ({
        ...l,
        label: `${l.personName || '(no person)'} — ${l.login} (group ${l.ladder})`,
      })),
      error: err => this.showError(err, 'Loading logins failed'),
    });
    this.docApi.getDocTypes().pipe(takeUntil(this.destroy$)).subscribe({
      next: types => this.docTypes = types,
      error: err => this.showError(err, 'Loading document types failed'),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---- selection ----------------------------------------------------------

  /** Selection is complete. Deliberately independent of `loading`: a busy state must never swallow a request. */
  get canLoad(): boolean {
    return !!this.fromDate && (!!this.selectedLogin || this.ladderOverride !== null);
  }

  /** `true` when the what-if ladder is set, so it wins over the picked login. */
  get overrideActive(): boolean { return this.ladderOverride !== null && this.ladderOverride !== undefined; }

  presetMonths(months: number): void { this.fromDate = HiddenDocumentsComponent.monthsAgo(months); }
  presetEra(): void { this.fromDate = new Date(1998, 0, 1); }

  onLoad(): void {
    if (!this.canLoad) { return; }
    this.applied = {
      login: this.selectedLogin?.login,
      ladder: this.ladderOverride,
      fromDate: HiddenDocumentsComponent.ymd(this.fromDate),
      docTypes: this.selectedDocTypes.map(t => t.DOCTYPEID),
    };
    this.first = 0;
    this.loadGen++;
    // A new Load changes the window: drop the old unlisted result; reload it only if the section is open.
    this.resetUnlisted();
    this.load();
    if (!this.unlistedCollapsed) { this.loadUnlisted(); }
  }

  onPage(event: { first?: number; rows?: number }): void {
    this.first = event.first ?? 0;
    this.rowsPerPage = event.rows ?? this.rowsPerPage;
    if (this.applied) { this.load(); }   // never auto-load before the first explicit Load
  }

  /** Requests the page for the applied snapshot at the current paginator position; replaces any in-flight request. */
  private load(): void {
    if (!this.applied) { return; }
    this.pageCancel$.next();
    const gen = this.loadGen;
    this.loading = true;
    this.api.getHiddenDocs({
      ...this.applied,
      start: this.first,
      fetch: this.rowsPerPage,
    }).pipe(takeUntil(merge(this.destroy$, this.pageCancel$))).subscribe({
      next: res => {
        this.result = res;
        this.resultGen = gen;
        this.rows = (res.rows || []).map(r => this.withRowFlags(r));
        this.shownFirst = this.first;
        this.shownRows = this.rowsPerPage;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        // Keep the paginator consistent with the rows still on screen.
        this.first = this.shownFirst;
        this.rowsPerPage = this.shownRows;
        this.showError(err, 'Loading hidden documents failed');
      },
    });
  }

  // ---- unlisted documents section ------------------------------------------

  /** First open of the section loads it; later toggles keep what is already shown. */
  onUnlistedToggle(collapsed: boolean): void {
    this.unlistedCollapsed = collapsed;
    if (!collapsed && !this.unlisted && !this.unlistedLoading) { this.loadUnlisted(); }
  }

  onUnlistedPage(event: { first?: number; rows?: number }): void {
    this.unlistedFirst = event.first ?? 0;
    this.unlistedRowsPerPage = event.rows ?? this.unlistedRowsPerPage;
    if (this.unlisted) { this.loadUnlisted(); }
  }

  private resetUnlisted(): void {
    this.unlistedCancel$.next();
    this.unlisted = null;
    this.unlistedRows = [];
    this.unlistedLoading = false;
    this.unlistedFirst = 0;
    this.unlistedShownFirst = 0;
  }

  /** Window and doc types come from the applied snapshot; login and ladder are deliberately not sent. */
  private loadUnlisted(): void {
    if (!this.applied) { return; }
    this.unlistedCancel$.next();
    const gen = this.loadGen;
    this.unlistedLoading = true;
    this.api.getUnlistedDocs({
      fromDate: this.applied.fromDate,
      docTypes: this.applied.docTypes,
      start: this.unlistedFirst,
      fetch: this.unlistedRowsPerPage,
    }).pipe(takeUntil(merge(this.destroy$, this.unlistedCancel$))).subscribe({
      next: res => {
        this.unlisted = res;
        this.unlistedGen = gen;
        this.unlistedRows = res.rows || [];
        this.unlistedShownFirst = this.unlistedFirst;
        this.unlistedShownRows = this.unlistedRowsPerPage;
        this.unlistedLoading = false;
      },
      error: err => {
        this.unlistedLoading = false;
        // Keep the paginator consistent with the rows still on screen.
        this.unlistedFirst = this.unlistedShownFirst;
        this.unlistedRowsPerPage = this.unlistedShownRows;
        this.showError(err, 'Loading unlisted documents failed');
      },
    });
  }

  get unlistedReasonCodes(): string[] { return this.unlisted ? Object.keys(this.unlisted.summary.byReason) : []; }

  /** The main results belong to an older Load than the current one and no request is in flight, i.e. the latest main
   *  load failed: the heading/table still describe the previous window and must be labelled (and not shown as current). */
  get mainStale(): boolean {
    return !!this.result && this.resultGen !== this.loadGen && !this.loading;
  }

  /** The main summary's listed count, but only when it belongs to the same Load as the unlisted result;
   *  otherwise `null` (e.g. the new main response has not arrived yet, or failed), never a figure from another window. */
  get listedForUnlisted(): number | null {
    return this.result && this.unlisted && this.resultGen === this.unlistedGen ? this.result.summary.totalInWindow : null;
  }

  /** Reconciliation of the two reads: documentsInWindow = listed + unlisted. They are separate requests, so a mismatch can be a change in between. */
  get reconciles(): boolean | null {
    const listed = this.listedForUnlisted;
    return listed === null || !this.unlisted ? null : this.unlisted.summary.documentsInWindow === listed + this.unlisted.summary.unlisted;
  }

  docTypeName(id: number): string {
    const t = this.docTypes.find(x => x.DOCTYPEID === id);
    return t ? (t.DOCNAME1 || t.DOCNAME) : ('type ' + id);
  }

  isUnlistedVoided(row: UnlistedDocsRow): boolean { return row.DOCACTIVE !== undefined && row.DOCACTIVE !== null && !row.DOCACTIVE; }

  /** Opens an unlisted document's edit page in a new tab (hash routing: via `Location.prepareExternalUrl()`).
   *  If the edit page cannot open a document with a missing client/person, that is itself informative. */
  openUnlistedDocument(row: UnlistedDocsRow): void {
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-document', row.DOCTYPEID, row.DOCID]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }

  // ---- presentation helpers ----------------------------------------------

  reasonLabel(code: string): string { return hiddenReasonLabel(code); }
  reasonTooltip(code: string): string { return hiddenReasonTooltip(code); }

  get reasonCodes(): string[] { return this.result ? Object.keys(this.result.summary.byReason) : []; }
  get topTypes() { return this.result ? this.result.summary.byType.slice(0, 5) : []; }

  /** Voided marker: the API deliberately does not filter DOCACTIVE. */
  isVoided(row: HiddenDocsRow): boolean { return row.DOCACTIVE !== undefined && row.DOCACTIVE !== null && !row.DOCACTIVE; }

  /** The two numbers that explain a ladder reason, e.g. "doc group 50000 vs login 10000". */
  reasonDetail(row: HiddenDocsRow): string {
    if (row.docGroup === null || row.docGroup === undefined) { return ''; }
    return `doc group ${row.docGroup} vs login ${this.result?.target.ladder}`;
  }

  /** Opens a document in a new tab. Hash routing: the path must go through `Location.prepareExternalUrl()`,
   *  or the new tab loses the `#` and lands on the server fallback. */
  openDocument(row: HiddenDocsRow): void {
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-document', row.DOCTYPEID, row.DOCID]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }

  openClient(row: HiddenDocsRow): void {
    if (!row.DOCCLIENTSID) { return; }
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-client', row.DOCCLIENTSID]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }

  /** Same row-state flags `ListDocumentsForClientComponent` derives, for the shared row classes. */
  private withRowFlags(dc: HiddenDocsRow): HiddenDocsRow {
    const now = new Date();
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart); dayEnd.setHours(23, 59, 59);
    const inv = dc.DOCTYPEID === 1 && dc.DOCSUM > 0;
    return {
      ...dc,
      fs: inv && Math.abs(dc.DOCSUM - dc.SHIPSUM) < 0.02,
      ps: inv && dc.SHIPSUM > 0 && (dc.DOCSUM - dc.SHIPSUM) > 0.02,
      fp: inv && Math.abs(dc.DOCSUM - dc.PAYDSUM) < 0.02,
      pp: inv && dc.PAYDSUM > 0 && Math.abs(dc.DOCSUM - dc.PAYDSUM) > 0.02,
      np: inv && (dc.SHIPSUM - dc.PAYDSUM) > 0.02,
      go: dc.DOCTYPEID === 1 && new Date(dc.DOCVALDATE) < now && dc.SHIPSUM === 0 && dc.PAYDSUM === 0,
      td: new Date(dc.DOCDATE) > dayStart && new Date(dc.DOCDATE) < dayEnd,
      fd: new Date(dc.DOCDATE) > dayEnd,
    };
  }

  // ---- export (current page only) ----------------------------------------

  onExportData(): void {
    import('xlsx').then(xlsx => {
      // Dates arrive as strings (CLAUDE.md "Date fields"): wrap in new Date(), never call .toISOString() on them.
      const exportData = this.rows.map(e => ({
        'Документ': e.DOCNAME,
        'Номер': e.DOCNUMBER,
        'Дата': e.DOCDATE ? new Date(e.DOCDATE) : null,
        'Продавец': e.LOCALCOMP,
        'Автор': e.AUTHOR,
        'Контрагент': e.PARTNERNAME,
        'Сумма': e.DOCSUM,
        'Оплачено': e.PAYDSUM,
        'Отгружено': e.SHIPSUM,
        'Doc group': e.docGroup,
        'Type default group': e.typeDefaultGroup,
        'Why hidden': (e.reasons || []).map(c => this.reasonLabel(c)).join('; '),
      }));
      const worksheet = xlsx.utils.json_to_sheet(exportData);
      const workbook = { Sheets: { hidden_documents: worksheet }, SheetNames: ['hidden_documents'] };
      const buffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' }); // xlsx's own untyped output
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(blob, 'hidden_documents_export_' + new Date().getTime() + '.xlsx');
    });
  }

  // ---- errors / date utils -----------------------------------------------

  private showError(err: any, summary: string): void { // HttpErrorResponse; body shape is the API's { error }
    const detail = err?.error?.error || err?.message || 'Unknown error';
    this.messageService.add({ severity: 'error', summary, detail: `${err?.status ?? ''} ${detail}`.trim(), life: 8000 });
  }

  private static monthsAgo(months: number): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setMonth(d.getMonth() - months);
    return d;
  }

  /** `YYYY-MM-DD` from local calendar parts (the picker's day), not UTC — avoids an off-by-one-day shift. */
  private static ymd(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }
}
